import { generatePath, useParams } from "react-router-dom";

import { useInsightView } from "@/hooks/useInsightView";
import useSearchParams from "@/hooks/useSearchParams";
import { useSearchSectionRoutes } from "@/hooks/useSearchSectionRoutes";
import { SearchFilterV4 } from "@/types/SearchFilter";
import {
  deleteNil,
  parseQuery,
  stringifyQuery,
  withDefaultDates,
} from "@/utils";

const SHEET_PARAM_PREFIX = "sheet_";

// Dates are left out on purpose, the sheet reads them from the path
const SHEET_FILTER_KEYS = [
  "channel_name",
  "search",
  "tag",
  "channel_type",
  "freetext",
  "language",
  "sentiment",
  "min_reach_threshold",
  "mention_kind",
  "sort_by",
  "insight_id",
  "insight_types",
] as const satisfies (keyof SearchFilterV4)[];

type SheetFilterKey = (typeof SHEET_FILTER_KEYS)[number];

export type SheetFilters = Pick<SearchFilterV4, SheetFilterKey>;

function isSheetFilterKey(key: string): key is SheetFilterKey {
  return (SHEET_FILTER_KEYS as readonly string[]).includes(key);
}

function toSheetKey(key: SheetFilterKey) {
  return `${SHEET_PARAM_PREFIX}${key}`;
}

const EMPTY_SHEET_PARAMS = Object.fromEntries(
  SHEET_FILTER_KEYS.map((key) => [toSheetKey(key), undefined])
);

export function toSheetParams(filters: Partial<SearchFilterV4>) {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([key]) => isSheetFilterKey(key))
      .map(([key, value]) => [toSheetKey(key as SheetFilterKey), value])
  );
}

function fromSheetParams(params: Record<string, unknown>): SheetFilters {
  return deleteNil(
    Object.fromEntries(
      SHEET_FILTER_KEYS.map((key) => [key, params[toSheetKey(key)]])
    )
  );
}

function withoutSheetParams(query: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(query).filter(([key]) => !key.startsWith(SHEET_PARAM_PREFIX))
  );
}

export function omitSheetParams(search: string) {
  const nextSearch = stringifyQuery(
    withoutSheetParams(parseQuery<Record<string, unknown>>(search))
  );

  return nextSearch ? `?${nextSearch}` : "";
}

/** Replaces the page's filters and dates with the sheet's, dropping the sheet
 * params and the page number.
 */
export function promoteSheetParams(
  search: string,
  dates: { start_date: string; end_date: string }
) {
  const query = parseQuery<Record<string, unknown>>(search);
  const pageQuery = Object.fromEntries(
    Object.entries(withoutSheetParams(query)).filter(
      ([key]) => !isSheetFilterKey(key) && key !== "page"
    )
  );
  const nextSearch = stringifyQuery(
    deleteNil({ ...pageQuery, ...fromSheetParams(query), ...dates })
  );

  return nextSearch ? `?${nextSearch}` : "";
}

export function useSheetFilterParams() {
  const [params, setParams] = useSearchParams<Record<string, unknown>>();

  const filters = fromSheetParams(params);

  const setFilters = (nextFilters: SearchFilterV4, clear = false) => {
    setParams({
      ...(clear ? EMPTY_SHEET_PARAMS : {}),
      ...toSheetParams(nextFilters),
      sheet_page: undefined,
    });
  };

  return [filters, setFilters] as const;
}

/** Builds a link to the mentions sheet. The sheet starts from the page's
 * filters, with `overrides` replacing individual keys.
 */
export function useSheetLink() {
  // URL state
  const insight = useInsightView();
  const section = useSearchSectionRoutes();
  const { uuid, orgUuid } = useParams<{ uuid: Uuid; orgUuid: Uuid }>();
  const [params] = useSearchParams<Record<string, unknown>>();

  // Helpers
  const route = {
    overview: section.detailPeriod,
    reach: section.insightsReachPeriod,
    sentiment: section.insightsSentimentPeriod,
    results: section.insightsResultsPeriod,
  }[insight];
  const pageQuery = withoutSheetParams(params);
  const pageFilters = withDefaultDates(pageQuery);

  return ({
    overrides,
    startDate,
    endDate,
  }: {
    overrides?: Partial<SearchFilterV4>;
    startDate?: string;
    endDate?: string;
  } = {}) => {
    const search = stringifyQuery(
      deleteNil({
        ...pageQuery,
        ...toSheetParams({ ...pageFilters, ...overrides }),
      })
    );

    return {
      pathname: generatePath(route, {
        startDate: startDate ?? pageFilters.start_date,
        endDate: endDate ?? pageFilters.end_date,
        uuid,
        orgUuid,
      }),
      search: search ? `?${search}` : "",
    };
  };
}
